'use client';

import React, { useEffect, useId, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type Transition } from 'framer-motion';

export interface AIAgent {
  id: string;
  name: string;
  type: 'crisis' | 'media' | 'social' | 'executive' | 'content' | 'general';
  description: string;
  expertise: string[];
}

export interface AgentHandoffTransition {
  fromAgentId: string;
  fromAgentName: string;
  toAgentId: string;
  toAgentName: string;
  timestamp: number;
}

interface AgentHandoffAnimationProps {
  selectedAgent: AIAgent | null;
  phase: 'selecting' | 'handoff' | 'processing' | 'complete';
  processingSteps?: string[];
  statusText?: string;
  lastHandoff?: AgentHandoffTransition;
  className?: string;
  sceneAgents?: AIAgent[];
  theme?: 'light' | 'dark' | 'auto';
}

interface ThemePalette {
  shell: string;
  shellBorder: string;
  halo: string;
  textPrimary: string;
  textMuted: string;
  textSubtle: string;
  loopBase: string;
  loopPrimary: string;
  loopSecondary: string;
  loopAccent: string;
  loopInfo: string;
  loopSuccess: string;
  loopHighlight: string;
}

type SceneAgent = {
  id: string;
  name: string;
  type: AIAgent['type'];
};

type ActivityItem = {
  id: string;
  key: string;
  agentName: string;
  action: string;
  label: string;
  phase: AgentHandoffAnimationProps['phase'];
  color: string;
};

type LoopPathTone =
  | 'base'
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'info'
  | 'success'
  | 'highlight';

type LoopPath = {
  d: string;
  tone: LoopPathTone;
  opacity?: number;
};

const HANDOFF_STEP_PATTERN = /(.+?)\s*->\s*(.+)/;
const MAX_ACTIVITY_ITEMS = 3;

const DARK_PALETTE: ThemePalette = {
  shell: '#090b12',
  shellBorder: 'rgba(255,255,255,0.14)',
  halo: 'rgba(125,92,255,0.22)',
  textPrimary: '#f8fafc',
  textMuted: '#a6adc2',
  textSubtle: '#727b96',
  loopBase: 'hsl(var(--foreground))',
  loopPrimary: 'hsl(var(--primary))',
  loopSecondary: 'hsl(var(--secondary))',
  loopAccent: 'hsl(var(--accent))',
  loopInfo: 'hsl(var(--info))',
  loopSuccess: 'hsl(var(--success))',
  loopHighlight: 'hsl(var(--primary-foreground))',
};

const LIGHT_PALETTE: ThemePalette = {
  shell: '#ffffff',
  shellBorder: 'rgba(15,23,42,0.12)',
  halo: 'rgba(105,56,239,0.16)',
  textPrimary: '#0f172a',
  textMuted: '#536176',
  textSubtle: '#7b8798',
  loopBase: 'hsl(var(--foreground))',
  loopPrimary: 'hsl(var(--primary))',
  loopSecondary: 'hsl(var(--secondary))',
  loopAccent: 'hsl(var(--accent))',
  loopInfo: 'hsl(var(--info))',
  loopSuccess: 'hsl(var(--success))',
  loopHighlight: 'hsl(var(--primary-foreground))',
};

const OUTER_LOOP_PATHS: LoopPath[] = [
  {
    d: 'm12.5 7.9c-0.5 0-0.8-0.6-0.5-1.2 4.4-3.3 9.6-5.5 16.2-5.9 0.8-0.1 1.2 1.2 0.3 1.3-5.3 0.3-10.2 1.9-15.5 5.6-0.1 0.1-0.3 0.2-0.5 0.2z',
    tone: 'base',
    opacity: 0.18,
  },
  {
    d: 'm12.5 7.8c-0.5 0-1-0.8-0.5-1.2 4.4-3.2 9.1-5.2 16.2-5.8 0.8-0.1 1.2 1.2 0.3 1.3-5.2 0.3-9.8 1.7-15.5 5.6-0.1 0.1-0.3 0.1-0.5 0.1z',
    tone: 'primary',
  },
  {
    d: 'm1.9 27.7c-0.4 0-0.7-0.3-0.7-0.7 0.6-6.1 3.6-12.2 7.7-17.5 0.7-0.8 1.7 0.1 1.2 0.9-4 4.1-7 10.1-7.6 16.7-0.1 0.4-0.3 0.6-0.6 0.6z',
    tone: 'secondary',
  },
  {
    d: 'm7.7 48.8c-0.2 0-0.3-0.1-0.5-0.3-3.4-4.4-5.9-10.5-6.1-17.4 0.2-0.8 1.3-1.1 1.3 0 0.2 5.9 2.5 11.6 6 16.5 0.5 0.5 0 1.3-0.7 1.2z',
    tone: 'secondary',
  },
  {
    d: 'm27.7 59.3c-5.4-0.3-11.6-2.4-17.5-7.5-0.6-0.7 0.2-1.8 1-1.1 4.4 3.8 9.7 6.5 16.6 7.1 0.8 0 1 1.3-0.1 1.5z',
    tone: 'accent',
  },
  {
    d: 'm31.7 59.3c-1-0.2-0.8-1.5 0.2-1.5 4.8-0.2 9.9-1.7 14.5-5.1 0.6-0.5 1.6 0.2 1 1-4 2.7-8.7 5.2-15.7 5.6z',
    tone: 'accent',
  },
  {
    d: 'm50.1 51.1c-0.7 0.2-1.3-0.7-0.6-1.3 4-3.9 7.1-9.4 8.1-17.1 0.3-0.7 1.3-0.7 1.3 0.2-0.6 6.5-3.3 13.2-8.3 18.1-0.1 0.1-0.3 0.1-0.5 0.1z',
    tone: 'info',
  },
  {
    d: 'm58.3 29.4c-0.3 0-0.6-0.3-0.6-0.7-0.3-5.7-2.3-11.1-6.2-16.4-0.6-0.5 0.2-1.7 1.2-1 3.6 4.5 6 9.8 6.3 17.4 0 0.4-0.4 0.7-0.7 0.7z',
    tone: 'info',
  },
  {
    d: 'm49.2 9.3c-0.2 0-0.4-0.1-0.5-0.2-4.2-3.5-9-6.1-16.5-6.9-0.8-0.2-0.8-1.4 0.2-1.4 6.4 0.4 11.5 2.2 17.4 7.2 0.6 0.5 0 1.4-0.6 1.3z',
    tone: 'primary',
  },
];

const MIDDLE_LOOP_PATHS: LoopPath[] = [
  {
    d: 'm30 52.5c-3.9 0-7.6-0.9-12.2-3.6-0.8-0.6-0.2-1.6 0.7-1.3 3.1 2 6.8 3.5 11.5 3.5 11.6 0 19.9-8.4 21.1-18.2 0.4-4.5-0.3-8.3-2.6-13.1-0.1-0.7 0.9-1.4 1.4-0.7 1.7 3.3 2.7 6.9 2.6 11-0.3 11.7-9.2 22.4-22.5 22.4z',
    tone: 'accent',
  },
  {
    d: 'm12.7 44.2c-0.2 0-0.4-0.1-0.5-0.3-2.8-3.5-4.6-8.2-4.6-13.9 0-11.1 9.6-22.6 22.3-22.6 5 0 10 1.3 16.4 6.9 0.5 0.3 0.1 1.4-0.8 1-4.1-3.9-8.8-6.5-15.5-6.5-10.9 0-21.1 9-21.1 21.2 0 4.7 1.5 9 4.5 13 0.3 0.5-0.1 1.3-0.7 1.2z',
    tone: 'success',
  },
];

const INNER_LOOP_PATHS: LoopPath[] = [
  {
    d: 'm28.7 45.7c-1.8-0.1-3.6-0.5-5.4-1.4-0.9-0.4-0.4-1.9 0.7-1.4 1.4 0.6 3.1 1.2 4.8 1.3 0.9 0.2 0.9 1.4-0.1 1.5z',
    tone: 'info',
  },
  {
    d: 'm32 45.6c-0.7 0-1.1-1.2-0.1-1.4 1.5-0.1 3.2-0.7 4.7-1.5 1-0.5 1.8 1 0.6 1.3-1.4 0.7-3.1 1.3-5.2 1.6z',
    tone: 'info',
  },
  {
    d: 'm39.7 41.9c-0.4 0-1-0.7-0.4-1.3 2.3-1.9 4.2-4.7 4.6-8.4 0.4-0.9 1.7-0.8 1.5 0.3-0.5 3.6-2.1 6.6-5.1 9.3-0.2 0.1-0.4 0.1-0.6 0.1z',
    tone: 'info',
  },
  {
    d: 'm44.8 29.7c-0.3-0.1-0.7-0.3-0.7-0.8-0.1-1.5-0.4-2.8-1-4.3-0.1-0.9 1.2-1.3 1.4-0.3 0.6 1.4 1 3.1 1 4.7 0 0.4-0.4 0.7-0.7 0.7z',
    tone: 'secondary',
  },
  {
    d: 'm42.1 22.1c-0.2 0-0.5-0.2-0.6-0.4-0.8-1-1.6-2-2.7-2.9-0.7-0.5 0-1.8 1-1.2 1.1 0.9 2.3 2 3 3.3 0.3 0.5-0.1 1.2-0.7 1.2z',
    tone: 'secondary',
  },
  {
    d: 'm36.4 17.1c-0.1 0-0.1 0-0.3-0.1-1.4-0.6-2.8-1.1-4.8-1.3-1 0-0.9-1.4 0.1-1.3 1.8 0.1 3.5 0.6 5.3 1.3 0.7 0.4 0.6 1.3-0.3 1.4z',
    tone: 'secondary',
  },
  {
    d: 'm23.5 17.2c-0.8 0-1.1-1.1-0.3-1.4 1.4-0.7 3.1-1.2 4.9-1.3 0.8 0 1.2 1.2 0.2 1.3-1.4 0.2-3 0.6-4.4 1.3-0.2 0.1-0.3 0.1-0.4 0.1z',
    tone: 'success',
  },
  {
    d: 'm18.1 21.7c-0.5 0-1-0.7-0.5-1.3 0.7-0.8 1.5-1.8 2.6-2.7 0.7-0.5 1.7 0.4 0.9 1.1-0.7 0.6-1.6 1.6-2.4 2.7-0.2 0.1-0.4 0.2-0.6 0.2z',
    tone: 'success',
  },
  {
    d: 'm15.3 28.3c-0.3 0-0.7-0.4-0.7-0.7 0.2-1.2 0.5-2.5 1.1-3.9 0.5-0.8 1.8-0.3 1.4 0.7-0.5 1-0.8 2.2-1 3.4-0.1 0.4-0.5 0.5-0.8 0.5z',
    tone: 'success',
  },
  {
    d: 'm16 35.9c-0.3 0-0.6-0.2-0.7-0.6-0.5-1.3-0.8-2.9-0.8-4.4 0.1-1 1.5-0.9 1.6 0 0 1.2 0.4 2.8 0.7 4.1 0.1 0.4-0.2 0.9-0.8 0.9z',
    tone: 'success',
  },
  {
    d: 'm20.6 42.2c-1.1-0.8-2.3-2.1-3.6-3.8-0.3-0.7 0.6-1.7 1.3-0.8 0.8 1.1 1.8 2.3 2.8 3.1 0.8 0.6 0.3 1.7-0.5 1.5z',
    tone: 'success',
  },
];

const slugifyAgentName = (value: string): string => {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'agent';
};

const getAgentNameKey = (value: string): string => slugifyAgentName(value);

const getSceneAgentIdentityKey = (agent: Partial<SceneAgent>): string =>
  getAgentNameKey(agent.name || agent.id || 'agent');

const themeSafeAgent = (agent: Partial<SceneAgent>): SceneAgent => {
  const fallbackName = agent.name || 'Agent';
  return {
    id: agent.id || slugifyAgentName(fallbackName),
    name: fallbackName,
    type: agent.type || 'general',
  };
};

const inferAgentType = (id: string, name: string): AIAgent['type'] => {
  const value = `${id} ${name}`.toLowerCase();
  if (value.includes('crisis')) return 'crisis';
  if (value.includes('media') || value.includes('pitch')) return 'media';
  if (value.includes('social')) return 'social';
  if (value.includes('executive')) return 'executive';
  if (value.includes('writer') || value.includes('draft') || value.includes('editor') || value.includes('review')) return 'content';
  return 'general';
};

const getCoreColor = (agentType: AIAgent['type']): string => {
  switch (agentType) {
    case 'crisis':
      return 'hsl(var(--destructive))';
    case 'media':
      return 'hsl(var(--info))';
    case 'social':
      return 'hsl(var(--primary))';
    case 'executive':
      return 'hsl(var(--success))';
    case 'content':
      return 'hsl(var(--warning))';
    default:
      return 'hsl(var(--secondary))';
  }
};

const getLoopColor = (palette: ThemePalette, tone: LoopPathTone): string => {
  switch (tone) {
    case 'primary':
      return palette.loopPrimary;
    case 'secondary':
      return palette.loopSecondary;
    case 'accent':
      return palette.loopAccent;
    case 'info':
      return palette.loopInfo;
    case 'success':
      return palette.loopSuccess;
    case 'highlight':
      return palette.loopHighlight;
    default:
      return palette.loopBase;
  }
};

const deriveAgentsFromProps = (
  selectedAgent: AIAgent | null,
  lastHandoff?: AgentHandoffTransition,
  processingSteps: string[] = []
): SceneAgent[] => {
  const orderedAgents: SceneAgent[] = [];
  const knownAgentsByName = new Map<string, SceneAgent>();

  const rememberKnownAgent = (agent?: Partial<SceneAgent> | null) => {
    if (!agent?.name && !agent?.id) return;
    const safeAgent = themeSafeAgent(agent);
    knownAgentsByName.set(getSceneAgentIdentityKey(safeAgent), safeAgent);
  };

  rememberKnownAgent(selectedAgent);
  rememberKnownAgent(
    lastHandoff
      ? {
          id: lastHandoff.fromAgentId,
          name: lastHandoff.fromAgentName,
        }
      : null
  );
  rememberKnownAgent(
    lastHandoff
      ? {
          id: lastHandoff.toAgentId,
          name: lastHandoff.toAgentName,
        }
      : null
  );

  const upsert = (name: string, id?: string, type?: AIAgent['type']) => {
    const nameKey = getAgentNameKey(name);
    const knownAgent = knownAgentsByName.get(nameKey);
    const next = themeSafeAgent({
      id: id || knownAgent?.id || slugifyAgentName(name),
      name: knownAgent?.name || name,
      type: type || knownAgent?.type || inferAgentType(id || knownAgent?.id || slugifyAgentName(name), name),
    });

    const existingIndex = orderedAgents.findIndex(
      (agent) => agent.id === next.id || getSceneAgentIdentityKey(agent) === nameKey
    );
    if (existingIndex >= 0) {
      orderedAgents[existingIndex] = next;
      return;
    }

    orderedAgents.push(next);
  };

  processingSteps.forEach((step) => {
    const match = step.match(HANDOFF_STEP_PATTERN);
    if (!match) return;
    upsert(match[1].trim());
    upsert(match[2].trim());
  });

  if (lastHandoff) {
    upsert(lastHandoff.fromAgentName, lastHandoff.fromAgentId);
    upsert(lastHandoff.toAgentName, lastHandoff.toAgentId);
  }

  if (selectedAgent) {
    upsert(selectedAgent.name, selectedAgent.id, selectedAgent.type);
  }

  return orderedAgents.slice(-3);
};

const mergeAgentsByDerivedOrder = (previousAgents: SceneAgent[], derivedAgents: SceneAgent[]): SceneAgent[] => {
  const merged: SceneAgent[] = [];
  const included = new Set<string>();

  derivedAgents.forEach((agent) => {
    const identityKey = getSceneAgentIdentityKey(agent);
    const previousAgent =
      previousAgents.find(
        (candidate) => candidate.id === agent.id || getSceneAgentIdentityKey(candidate) === identityKey
      ) ?? null;

    merged.push(previousAgent ? { ...previousAgent, ...agent } : agent);
    included.add(identityKey);
  });

  previousAgents.forEach((agent) => {
    const identityKey = getSceneAgentIdentityKey(agent);
    if (included.has(identityKey)) return;
    merged.push(agent);
  });

  return merged.slice(-3);
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const sentenceCase = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
};

const fallbackActionForPhase = (phase: AgentHandoffAnimationProps['phase']): string => {
  switch (phase) {
    case 'selecting':
      return 'Selecting the best agent route.';
    case 'handoff':
      return 'Accepting the next handoff.';
    case 'complete':
      return 'Completed the current handoff.';
    default:
      return 'Processing the request.';
  }
};

const cleanActionText = (
  statusLine: string,
  agentName: string,
  phase: AgentHandoffAnimationProps['phase']
): string => {
  const normalized = statusLine.replace(/\s+/g, ' ').trim();
  if (!normalized) return fallbackActionForPhase(phase);

  const runningAgentPattern = new RegExp(`^running\\s+${escapeRegExp(agentName)}\\s*(?:while\\s*)?`, 'i');
  const withoutAgent = normalized.replace(runningAgentPattern, '').replace(/^[-:.;,\s]+/, '').trim();

  if (!withoutAgent || withoutAgent.toLowerCase() === agentName.toLowerCase()) {
    return fallbackActionForPhase(phase);
  }

  return sentenceCase(withoutAgent);
};

const getActivityLabel = (
  phase: AgentHandoffAnimationProps['phase'],
  activeAgent: SceneAgent,
  lastHandoff?: AgentHandoffTransition
): string => {
  if (phase === 'selecting') return 'Selecting route';
  if (phase === 'complete') return 'Handoff complete';
  if (lastHandoff && lastHandoff.fromAgentName !== lastHandoff.toAgentName) {
    return `${lastHandoff.fromAgentName} -> ${lastHandoff.toAgentName}`;
  }
  return `${activeAgent.name} active`;
};

const buildActivityItem = (
  phase: AgentHandoffAnimationProps['phase'],
  activeAgent: SceneAgent,
  statusLine: string,
  lastHandoff?: AgentHandoffTransition
): ActivityItem => {
  const key = [
    phase,
    activeAgent.id,
    lastHandoff?.timestamp ?? 'none',
    statusLine,
  ].join(':');

  return {
    id: key,
    key,
    agentName: phase === 'selecting' && activeAgent.name === 'Awaiting' ? 'Coordinator' : activeAgent.name,
    action: cleanActionText(statusLine, activeAgent.name, phase),
    label: getActivityLabel(phase, activeAgent, lastHandoff),
    phase,
    color: getCoreColor(activeAgent.type),
  };
};

interface LoopHandoffMarkProps {
  palette: ThemePalette;
  activeColor: string;
  title: string;
  uid: string;
}

const LoopHandoffMark: React.FC<LoopHandoffMarkProps> = ({ palette, activeColor, title, uid }) => {
  const cssId = `handoff-loop-${uid}`;

  const renderPaths = (paths: LoopPath[]) =>
    paths.map((path, index) => (
      <path
        key={`${path.tone}-${index}`}
        d={path.d}
        fill={getLoopColor(palette, path.tone)}
        opacity={path.opacity ?? 1}
      />
    ));

  return (
    <svg
      className="h-full w-full overflow-visible"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="-8 -8 76 76"
      role="img"
      aria-label={title}
      focusable="false"
    >
      <style>
        {`
          .${cssId}-outer,
          .${cssId}-middle,
          .${cssId}-inner,
          .${cssId}-core {
            transform-box: fill-box;
            transform-origin: center;
            will-change: transform, opacity;
          }

          .${cssId}-outer {
            animation: ${cssId}-clockwise 5.6s linear infinite;
          }

          .${cssId}-middle {
            animation: ${cssId}-counter 4.2s linear infinite;
          }

          .${cssId}-inner {
            animation: ${cssId}-clockwise 2.8s linear infinite;
          }

          .${cssId}-core {
            animation: ${cssId}-pulse 1.6s ease-in-out infinite;
          }

          @keyframes ${cssId}-clockwise {
            to { transform: rotate(360deg); }
          }

          @keyframes ${cssId}-counter {
            to { transform: rotate(-360deg); }
          }

          @keyframes ${cssId}-pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(0.86); opacity: 0.72; }
          }

          @media (prefers-reduced-motion: reduce) {
            .${cssId}-outer,
            .${cssId}-middle,
            .${cssId}-inner,
            .${cssId}-core {
              animation: none;
            }
          }
        `}
      </style>

      <circle cx="30" cy="30" r="33" fill={palette.halo} />
      <circle cx="30" cy="30" r="24" fill={palette.shell} stroke={palette.shellBorder} strokeWidth="0.8" opacity="0.82" />

      <g className={`${cssId}-outer`}>{renderPaths(OUTER_LOOP_PATHS)}</g>
      <g className={`${cssId}-middle`}>{renderPaths(MIDDLE_LOOP_PATHS)}</g>
      <g className={`${cssId}-inner`}>{renderPaths(INNER_LOOP_PATHS)}</g>

      <g className={`${cssId}-core`}>
        <circle cx="30" cy="30" r="6.8" fill={palette.shell} stroke={activeColor} strokeWidth="1.8" />
        <circle cx="30" cy="30" r="3.4" fill={activeColor} />
        <circle cx="31.4" cy="27.8" r="1" fill={palette.loopHighlight} opacity="0.88" />
      </g>
    </svg>
  );
};

export const AgentHandoffAnimation: React.FC<AgentHandoffAnimationProps> = ({
  selectedAgent,
  phase,
  processingSteps = [],
  statusText,
  lastHandoff,
  className = '',
  sceneAgents,
  theme = 'auto',
}) => {
  const [isDark, setIsDark] = useState(true);
  const [seenAgents, setSeenAgents] = useState<SceneAgent[]>([]);
  const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);
  const shouldReduceMotion = useReducedMotion();
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  const derivedAgents = useMemo<SceneAgent[]>(() => {
    if (sceneAgents && sceneAgents.length > 0) {
      return sceneAgents.map((agent) => themeSafeAgent(agent)).slice(-3);
    }
    return deriveAgentsFromProps(selectedAgent, lastHandoff, processingSteps);
  }, [lastHandoff, processingSteps, sceneAgents, selectedAgent]);

  useEffect(() => {
    if (theme === 'dark') {
      setIsDark(true);
      return;
    }

    if (theme === 'light') {
      setIsDark(false);
      return;
    }

    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [theme]);

  useEffect(() => {
    if (phase === 'selecting' && !selectedAgent && processingSteps.length === 0) {
      setSeenAgents([]);
      return;
    }
    if (derivedAgents.length === 0) return;
    setSeenAgents((prev) => mergeAgentsByDerivedOrder(prev, derivedAgents));
  }, [derivedAgents, phase, processingSteps.length, selectedAgent]);

  const palette = isDark ? DARK_PALETTE : LIGHT_PALETTE;

  const visibleAgents = useMemo<SceneAgent[]>(() => {
    if (seenAgents.length > 0) return seenAgents.slice(-3);
    if (derivedAgents.length > 0) return derivedAgents.slice(-3);
    if (selectedAgent) return [themeSafeAgent(selectedAgent)];
    return [{ id: 'system', name: 'Awaiting', type: 'general' }];
  }, [derivedAgents, seenAgents, selectedAgent]);

  const activeAgent = useMemo<SceneAgent>(() => {
    if (selectedAgent) return themeSafeAgent(selectedAgent);

    if (lastHandoff) {
      const handoffAgent = visibleAgents.find(
        (agent) =>
          agent.id === lastHandoff.toAgentId ||
          getAgentNameKey(agent.name) === getAgentNameKey(lastHandoff.toAgentName)
      );
      if (handoffAgent) return handoffAgent;
      return themeSafeAgent({
        id: lastHandoff.toAgentId,
        name: lastHandoff.toAgentName,
      });
    }

    return visibleAgents[visibleAgents.length - 1] ?? { id: 'system', name: 'Awaiting', type: 'general' };
  }, [lastHandoff, selectedAgent, visibleAgents]);

  const statusLine =
    (statusText || processingSteps[processingSteps.length - 1] || '').trim() ||
    fallbackActionForPhase(phase);

  const currentActivity = useMemo(
    () => buildActivityItem(phase, activeAgent, statusLine, lastHandoff),
    [activeAgent, lastHandoff, phase, statusLine]
  );

  useEffect(() => {
    setActivityItems((previous) => {
      if (previous[previous.length - 1]?.key === currentActivity.key) return previous;
      return [...previous, currentActivity].slice(-MAX_ACTIVITY_ITEMS);
    });
  }, [currentActivity]);

  const visibleActivityItems = activityItems.length > 0 ? activityItems : [currentActivity];
  const activeColor = getCoreColor(activeAgent.type);
  const transition: Transition = shouldReduceMotion ? { duration: 0 } : { duration: 0.28, ease: 'easeOut' };

  return (
    <div className={`w-full ${className}`} role="status" aria-live="polite">
      <div className="flex w-full max-w-[620px] flex-col gap-4 sm:flex-row sm:items-center">
        <div className="mx-auto h-24 w-24 shrink-0 sm:mx-0">
          <LoopHandoffMark
            palette={palette}
            activeColor={activeColor}
            title={`${currentActivity.agentName}: ${currentActivity.action}`}
            uid={uid || 'agent'}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: activeColor, boxShadow: `0 0 18px ${activeColor}` }}
              aria-hidden="true"
            />
            Agent handoff
          </div>

          <div className="flex h-[106px] flex-col justify-end gap-2 overflow-hidden">
            <AnimatePresence initial={false}>
              {visibleActivityItems.map((item, index) => {
                const distanceFromLatest = visibleActivityItems.length - 1 - index;
                const isCurrent = distanceFromLatest === 0;
                const itemOpacity = isCurrent ? 1 : Math.max(0.24, 0.54 - distanceFromLatest * 0.12);

                return (
                  <motion.div
                    layout
                    key={item.id}
                    initial={shouldReduceMotion ? false : { opacity: 0, y: 16 }}
                    animate={{ opacity: itemOpacity, y: 0, scale: isCurrent ? 1 : 0.985 }}
                    exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -20 }}
                    transition={transition}
                    className="min-w-0 border-l-2 pl-3"
                    style={{ borderColor: item.color }}
                  >
                    <div
                      className="truncate text-sm font-semibold leading-5"
                      style={{ color: isCurrent ? palette.textPrimary : palette.textMuted }}
                    >
                      {item.agentName}
                    </div>
                    <div
                      className="line-clamp-2 break-words text-sm leading-5"
                      style={{ color: isCurrent ? palette.textMuted : palette.textSubtle }}
                    >
                      {item.action}
                    </div>
                    <div
                      className="mt-0.5 truncate text-xs leading-4"
                      style={{ color: palette.textSubtle }}
                    >
                      {item.label}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentHandoffAnimation;
